/**
 * CR-5479: shared OTIF promise-date variance classifier.
 *
 * A supplier promise date is compared against the Original Due Date (ODD =
 * the PO line's odd_date, the date OTIF measures against) and the supplier's OTIF tolerance window
 * (days_early / days_late). The result drives amber (early) / red (late)
 * colour coding plus a "(+N days late)" / "(-N days early)" variance suffix,
 * shown identically in the PO Line Items table and the Change Request views.
 */

export interface OtifWindow {
  days_early: number;
  days_late: number;
}

export interface OtifDateVariance {
  /** Outside the window early, outside late, or acceptable. */
  status: 'early' | 'late' | 'within';
  /** Signed day delta (promise − ODD); negative = earlier than ODD. */
  diffDays: number;
  /** Tailwind text+bg classes for the badge; '' when within the window. */
  className: string;
  /** e.g. ' (+156 days late)' / ' (-41 days early)'; '' when within. */
  suffix: string;
}

/**
 * Classify a supplier promise date against the ODD and OTIF window.
 * Returns null when either date is missing or unparseable so callers can
 * fall back to plain rendering.
 *
 * CR-6543: no hardcoded fallback window. The backend now resolves the
 * tenant's real tolerance window into `supplier_otif_window`; if a payload
 * ever arrives without one, we render plain rather than paint a line
 * early/late against a 21/1 guess the tenant may not use.
 */
export function getOtifDateVariance(
  promiseDate?: string | null,
  odd?: string | null,
  window?: OtifWindow | null,
): OtifDateVariance | null {
  if (!promiseDate || !odd) return null;
  const promise = new Date(promiseDate);
  const due = new Date(odd);
  if (Number.isNaN(promise.getTime()) || Number.isNaN(due.getTime())) return null;

  if (!window) return null;
  const w = window;
  const diffDays = Math.round((promise.getTime() - due.getTime()) / 86_400_000);

  if (diffDays < -w.days_early) {
    return {
      status: 'early',
      diffDays,
      className: 'text-amber-700 bg-amber-50',
      suffix: ` (${diffDays} days early)`,
    };
  }
  if (diffDays > w.days_late) {
    return {
      status: 'late',
      diffDays,
      className: 'text-red-700 bg-red-50',
      suffix: ` (+${diffDays} days late)`,
    };
  }
  return { status: 'within', diffDays, className: '', suffix: '' };
}
